import { Phone, Car, BedDouble, MapPin, ExternalLink, AlertTriangle, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/PageHeader";
import { cedi } from "@/lib/api";

const Stock = ({ n }) => (
  <span className={`font-semibold ${n <= 3 ? "text-[#E60000]" : "text-emerald-700"}`}>{n <= 3 ? `Only ${n} left` : `${n} left`}</span>
);

const HotelLocation = ({ h, prefix = "reg" }) => {
  const location = (h.location || "").trim();
  if (!location) return null;
  let map = false;
  try { map = ["http:", "https:"].includes(new URL(location).protocol); } catch { /* Plain address or coordinates. */ }
  return <div className="mt-3 text-xs text-gray-700 min-w-0" data-testid={`${prefix}-hotel-location-${h.id}`}>
    <div className="inline-flex items-center gap-1 font-semibold"><MapPin className="w-3.5 h-3.5 text-[#E60000]" />GPS Location</div>
    {map ? <a href={location} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-start gap-1 text-[#B30000] underline break-all" data-testid={`${prefix}-hotel-map-${h.id}`}><ExternalLink className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>Open map · {location}</span></a>
      : <p className="mt-1 break-words" data-testid={`${prefix}-hotel-location-text-${h.id}`}>{location}</p>}
  </div>;
};

const HotelInfo = ({ h, booking, prefix = "reg" }) => (
  <>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div><div className="font-semibold">{h.name}</div><div className="text-xs text-gray-500">{h.kind}</div></div>
      {h.drive_minutes > 0 && <span className="text-xs text-gray-600 inline-flex items-center gap-1 whitespace-nowrap" data-testid={`${prefix}-hotel-drive-${h.id}`}><Car className="w-3.5 h-3.5 text-[#E60000]" />{h.drive_minutes} min drive to venue</span>}
    </div>
    {h.facilities.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{h.facilities.map((f) => <span key={f} className="text-[11px] rounded-full bg-[#F3F4F6] px-2 py-0.5">{f}</span>)}</div>}
    <div className="mt-2 space-y-0.5">{h.rooms.map((r) => <div key={r.id} className="flex justify-between gap-2 text-xs" data-testid={`${prefix}-room-line-${r.id}`}><span className="text-gray-600">{r.name}{booking && <> · <Stock n={r.remaining} /></>}</span><span className="font-mono whitespace-nowrap">{cedi(r.rate)} / night</span></div>)}</div>
  </>
);

const DisplayList = ({ hotels, prefix = "reg", callToBook = true }) => (
  <div className="grid sm:grid-cols-2 gap-3" data-testid={`${prefix}-accommodation-display`}>
    {hotels.map((h) => (
      <div key={h.id} className="rounded-lg border border-slate-200 bg-white p-4 min-w-0 text-slate-900" data-testid={`${prefix}-hotel-card-${h.id}`}>
        <HotelInfo h={h} prefix={prefix} />
        <HotelLocation h={h} prefix={prefix} />
        {!h.rooms.length && <p className="text-xs text-slate-600 mt-2">Room details are not listed. Contact the hotel for availability and rates.</p>}
        {h.phone && /\d/.test(h.phone) ? <a href={`tel:${h.phone.replace(/[^+\d]/g, "")}`} className="mt-4 inline-flex items-center gap-2 min-h-11 px-3 py-2 rounded-md border border-slate-300 text-sm font-semibold text-[#B30000] bg-white hover:bg-red-50 hover:border-red-300 transition-colors break-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E60000]" data-testid={`${prefix}-hotel-call-${h.id}`}><Phone className="w-4 h-4 shrink-0" />{callToBook ? "Call" : "Contact"} {h.phone}{callToBook ? " to book" : ""}</a>
          : <p className="mt-3 text-sm text-slate-600" data-testid={`${prefix}-hotel-no-phone-${h.id}`}>A contact number has not been listed for this hotel.</p>}
      </div>
    ))}
  </div>
);

export const CallToBookNotice = ({ prefix = "reg" }) => (
  <div role="note" aria-labelledby={`${prefix}-call-booking-heading`} className="flex items-start gap-3 rounded-lg border border-amber-300 border-l-4 border-l-amber-500 bg-amber-50 p-4 mb-4 text-amber-950" data-testid={`${prefix}-call-booking-alert`}>
    <AlertTriangle className="w-6 h-6 shrink-0 text-amber-800" aria-hidden="true" />
    <div><h3 id={`${prefix}-call-booking-heading`} className="font-heading font-bold text-base">Call the hotel and make your own booking</h3>
      <p className="text-sm leading-relaxed mt-1">You must contact your chosen hotel directly to arrange accommodation. Submitting your event registration does not reserve a room.</p></div>
  </div>
);

export const AccommodationInfo = ({ event, prefix = "accommodation-info" }) => {
  const enabled = ["booking", "display"].includes(event?.accommodation_mode);
  const hotels = enabled ? (event.hotels || []).filter((h) => !h.hidden).map((h) => ({ ...h, rooms: (h.rooms || []).filter((r) => !r.hidden) })) : [];
  return <section data-testid={`${prefix}-view`}>
    <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-3" data-testid={`${prefix}-view-only`}><Info className="w-4 h-4" />Information only · no booking is made in this view</p>
    {event?.accommodation_mode === "display" && <CallToBookNotice prefix={prefix} />}
    {event?.accommodation_mode === "booking" && <p className="text-sm text-slate-700 mb-4" data-testid={`${prefix}-portal-note`}>Room bookings can be selected during event registration. This view only shows hotel details and rates.</p>}
    {hotels.length ? <DisplayList hotels={hotels} prefix={prefix} callToBook={event.accommodation_mode === "display"} />
      : <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700" data-testid={`${prefix}-empty`}>No accommodation options are currently listed for this event.</p>}
  </section>;
};

const BookingPicker = ({ hotels: all, value, onChange }) => {
  const hotels = all.map((h) => ({ ...h, rooms: h.rooms.filter((r) => r.remaining > 0) })).filter((h) => h.rooms.length);
  const hotel = hotels.find((h) => h.id === value.hotelId);
  const pickHotel = (id) => {
    const h = hotels.find((x) => x.id === id);
    onChange({ hotelId: id, roomId: h?.rooms[0]?.id || "", nights: id ? Math.max(value.nights, 1) : 0 });
  };
  return (
    <div data-testid="reg-accommodation-booking">
      <div className="grid sm:grid-cols-2 gap-3">
        <button type="button" onClick={() => pickHotel("")} data-testid="reg-hotel-none"
          className={`text-left rounded-md border p-4 text-sm transition-colors ${!value.hotelId ? "border-[#E60000] bg-[#FFF0F0]" : "border-[#E5E7EB] hover:border-gray-400"}`}>
          <div className="font-semibold">No accommodation</div><div className="text-xs text-gray-500">I’ll arrange my own stay</div>
        </button>
        {hotels.map((h) => (
          <div key={h.id} className={`rounded-md border text-sm transition-colors min-w-0 ${value.hotelId === h.id ? "border-[#E60000] bg-[#FFF0F0]" : "border-[#E5E7EB] hover:border-gray-400"}`}>
            <button type="button" onClick={() => pickHotel(h.id)} data-testid={`reg-hotel-option-${h.id}`} aria-pressed={value.hotelId === h.id} className="w-full text-left p-4 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E60000]">
              <HotelInfo h={h} booking />
            </button>
            {h.location?.trim() && <div className="px-4 pb-4"><HotelLocation h={h} /></div>}
          </div>
        ))}
      </div>
      {!hotels.length && <p className="text-sm text-gray-500 mt-3" data-testid="reg-accommodation-sold-out">All rooms are fully booked.</p>}
      {hotel && (
        <div className="grid sm:grid-cols-[1fr_140px] gap-3 mt-4">
          <Select value={value.roomId} onValueChange={(roomId) => onChange({ ...value, roomId })}>
            <SelectTrigger className="h-10" data-testid="reg-room-select"><SelectValue placeholder="Room type" /></SelectTrigger>
            <SelectContent>{hotel.rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {cedi(r.rate)}/night · {r.remaining} left</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm"><BedDouble className="w-4 h-4 text-gray-500" />
            <Input type="number" min="1" max="30" value={value.nights || ""} onChange={(e) => onChange({ ...value, nights: Math.min(30, Math.max(0, Number(e.target.value) || 0)) })}
              onBlur={() => !value.nights && onChange({ ...value, nights: 1 })} className="h-10 font-mono" data-testid="reg-nights-input" />nights
          </label>
        </div>
      )}
    </div>
  );
};

export const AccommodationStep = ({ ev, value, onChange, step }) => {
  if (!ev?.hotels?.length || !["booking", "display"].includes(ev.accommodation_mode)) return null;
  const booking = ev.accommodation_mode === "booking";
  return (
    <Panel title={`${step}. Accommodation`} testId="reg-step-accommodation"
      right={<span className="text-xs text-gray-500">{booking ? "Book through the portal" : "Call the hotel to book"}</span>}>
      {booking ? <BookingPicker hotels={ev.hotels} value={value} onChange={onChange} /> : <><CallToBookNotice /><DisplayList hotels={ev.hotels} /></>}
    </Panel>
  );
};

export const accommodationCost = (ev, v) => {
  if (ev?.accommodation_mode !== "booking" || !v.hotelId) return null;
  const h = ev.hotels.find((x) => x.id === v.hotelId);
  const r = h?.rooms.find((x) => x.id === v.roomId);
  return r && r.remaining > 0 && v.nights > 0 ? { hotel: h.name, room: r.name, nights: v.nights, amount: r.rate * v.nights } : null;
};
